<template>
  <v-card height="100%" class="d-flex flex-column">
    <v-card-title>
      <span v-if="isInitialized && bom && bom.job">BOM For Job {{ bom.job.code }} {{ bom.job.project.name }} {{ bom.job.reference }}</span>
      <span v-if="isInitialized && bom == null">BOM Does Not Exist</span>
    </v-card-title>
    <v-card-text style="overflow:scroll; height:100%">
      <v-dialog v-model="showItemForm" persistent width="50%">
        <v-card>
          <v-card-title>
            <span v-if="itemId === null">New Item</span>
            <span v-else>Update Item</span>
          </v-card-title>
          <v-card-text>
            <v-row class="mt-2">
              <v-col cols="6">
                <inventory-class-select-element 
                  v-model="itemClassId" 
                  :disabled="itemId !== null"
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete 
                  v-model="itemSubclassIds" 
                  label="Sub Classes"
                  :disabled="itemId !== null"
                  :items="filteredSubClassesList"
                  item-text="name"
                  item-value="id"
                  multiple
                >
                  <template #item="{ item }">
                    <v-chip
                      class="mt-1 mb-1"
                      small
                      text-color="white"
                      style="cursor: pointer;"
                      :color="item ? item.color : 'white'"
                    >
                      {{ item ? item.name : '' }}
                    </v-chip>
                  </template>
                  <template #selection="{ item }">
                    <v-chip
                      class="mt-1 mb-1"
                      small
                      text-color="white"
                      style="cursor: pointer;"
                      :color="item ? item.color : 'white'"
                    >
                      {{ item ? item.name : '' }}
                    </v-chip>
                  </template>
              </v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="8">
                <v-autocomplete 
                  v-model="itemInventoryItemId" 
                  :items="inventoryItems" 
                  :disabled="itemId !== null"
                  item-value="id" 
                  :item-text="item => `${item.material_id}: ${item.name}`" 
                  hide-details 
                  class="pt-0"
                >
                  <template #label>Inventory Item<red-asterisk/></template>
                  <template #item="{ item }">
                    <span class="font-weight-bold">{{ item.material_id }}</span>:
                    {{ item.name }}
                  </template>
                  <template #selection="{ item }">
                    <span class="font-weight-bold">{{ item.material_id }}</span>:
                    {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-autocomplete 
                  v-model="itemColorId" 
                  label="Color" 
                  :items="colors" 
                  item-value="id" 
                  hide-details 
                  class="pt-0">
                  <template #item="{ item }">
                    {{ item.code }}: {{ item.name }}
                  </template>
                  <template #selection="{ item }">
                    {{ item.code }}: {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-autocomplete v-model="itemLengthId" :items="lengths" item-value="id" item-text="name" hide-details class="pt-0">
                  <template #label>Length</template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-autocomplete v-model="itemUomId" :items="uoms" item-value="id" item-text="name" hide-details class="pt-0">
                  <template #label>UOM<red-asterisk/></template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model="itemQuantity" hide-details class="pt-0">
                  <template #label>Quantity Required<red-asterisk/></template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeItemForm" color="info" outlined class="mr-2" >CLOSE</v-btn>
            <v-btn @click="addItem" color="success" outlined :loading="itemIsBeingAdded">{{ this.itemId === null ? 'ADD' : 'UPDATE' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-row class="mt-2">
        <v-col cols="10">

        </v-col>

        <v-col class="text-right">
          <v-btn v-if="canEdit" color="primary" outlined @click="openItemForm">
            <v-icon small class="mr-1">
              fas fa-plus
            </v-icon>
            New Item
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-card outlined>
            <v-data-table
              v-if="isInitialized"
              :headers="headers"
              :items="bomItems"
              hide-default-footer
              :sortable="false"
              :items-per-page="-1"
              dense
            >
              <template #item="{ item }">
                <tr v-if="!item.is_deleted" class="py-1">
                  <td class="text-center">{{ item.inventory_item.material_id || '' }}</td>
                  <td>
                    <v-img 
                      v-if="item.photo" 
                      class="mx-auto"
                      @click="openItemShowImageForm(item)"
                      :src="`data:${item.photo.photo_mime};base64, ${item.photo.photo}`" 
                      style="width:50px"
                      />
                  </td>
                  <td class="text-center">{{ item.inventory_item.name || '' }}</td>
                  <td class="text-center">{{ item.color ? item.color.name : '' }}</td>
                  <td class="text-center">{{ item.uom ? item.uom.name : '' }}</td>
                  <td class="text-center">{{ item.length ? item.length.name : '' }}</td>
                  <td class="text-center">{{ Number(item.quantity) }}</td>
                  <td class="text-right">
                    <v-btn v-if="canEdit" @click="openItemForm(item)" color="primary" class="mr-2" small outlined>Edit</v-btn>
                    <v-btn v-if="canEdit" @click="deleteItem(item)" color="error" small outlined>Delete</v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="bom">
        <div v-if="bom.import_errors.length > 0">
          <v-row>
            <v-col cols="12">
              <div class="text-h6">
                <v-btn small icon @click="showErrorsTable = !showErrorsTable">
                  <v-icon>{{ showErrorsTable ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                </v-btn>
                <span class="text-h6">Item(s) Failed To Import</span>
              </div>

                <v-data-table
                  v-if="isInitialized && showErrorsTable"
                  class="text-red"
                  :headers="errorItemsHeaders"
                  :items="bom.import_errors"
                  hide-default-footer
                  :items-per-page="-1"
                  dense
                >
                  
                  <template #item="{ item }">
                    <tr v-if="item.is_deleted === 0">
                      <td class="text-center">{{ item.line_number }}</td>
                      <td class="text-center">{{ item.material_id }}</td>
                      <td class="text-center">{{ item.class }}</td>
                      <td class="text-center">{{ item.sub_class }}</td>
                      <td class="text-center">{{ item.work_order }}</td>
                      <td class="text-center">{{ item.description }}</td>
                      <td class="text-center">{{ item.ext_color_code }}</td>
                      <td class="text-center">{{ item.ext_color_description }}</td>
                      <td class="text-center">{{ item.int_color_code }}</td>
                      <td class="text-center">{{ item.int_color_description }}</td>
                      <td class="text-center">{{ item.qty }}</td>
                      <td class="text-center">{{ item.category }}</td>
                      <td class="text-center">{{ item.reason }}</td>
                    </tr>
                  </template>
                  <template #footer>
                    <tr>
                    </tr>
                  </template>
                </v-data-table>
            </v-col>
          </v-row>
        </div>
        <v-row v-if="!isManual">
          <v-col>
            {{ bom.items.length }} Item(s) Imported Successfully / {{ bom.import_errors.length }} Item(s) Failed
          </v-col>
        </v-row>
      </div>

      <confirm-form ref="confirmForm" />
      <InventoryItemShowImageForm ref="itemShowImageForm" />

    </v-card-text>
    <v-card-actions>
      <!-- This button is to fix an annoying UI issue -->
      <v-btn v-show="false"></v-btn>
      <v-spacer />
      <v-btn @click="closeForm" color="info" outlined class="mr-2">CLOSE</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import * as bomApi from '@/api/BomApi.js';
import InventoryItemShowImageForm from '../../components/forms/InventoryItemShowImageForm.vue';

export default {
  emit: ['close'],
  components: {
    InventoryItemShowImageForm
  },
  data: () => ({
    bom: null,
    headers: [
      { text: 'Item Code', align: 'center', sortable: false, class: 'px-0', width: '5%' },
      { text: 'Picture', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Item Name', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Colour', align: 'center', sortable: false, class: 'px-0', width: '10%' },
      { text: 'UOM', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Length', align: 'center', sortable: false, class: 'px-0', width: '10%' },
      { text: 'Qty', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Actions', align: 'end', sortable: false, class: 'px-0' },
    ],

    errorItemsHeaders: [
      { text: 'Line Number', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Material Id', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Class', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Sub Class', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Work Order', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Ext Color Code', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Ext Color Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Int Color Code', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Int Color Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Quantity', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Category', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Reason', align: 'center', sortable: false, class: 'px-0' },
    ],
    isInitialized: false,

    subclassesList: [],
    filteredSubClassesList: [],
    itemClassId: null,
    itemSubclassIds: [],
    inventoryItems: [],
    colors: [],
    classToSubclassesMap: {},
    showItemForm: false,
    itemInventoryItemId: null,
    itemColorId: null,
    itemLengthId: null,
    itemUomId: null,
    itemQuantity: null,
    itemId: null,
    itemIsBeingAdded: false,
    showErrorsTable: false,
    
  }),
  created() {
    Promise.all([
      this.$api.color.read(),
      this.$api.inventorySubClass.getList(),
    ]).then(([colors, subclassesList, lengths]) => {
      this.colors = colors;

      this.subClassesList = subclassesList
      this.classToSubclassesMap = this.subClassesList.reduce((acc, x) => {
        if (!(x.inventory_class_id in acc)) acc[x.inventory_class_id] = [];
        acc[x.inventory_class_id].push(x);
        return acc;
      }, {});
      this.filteredSubClassesList = this.itemClassId ? this.classToSubclassesMap[this.itemClassId] : this.subClassesList;
      this.getInventoryItems();
    })
  },
  watch: {
    itemClassId(value) {
      this.filteredSubClassesList = value ? this.classToSubclassesMap[value] : this.subClassesList;
      if (value !== null && value !== 0) this.itemSubclassIds = this.itemSubclassIds.filter(x => this.filteredSubClassesList.findIndex(y => x === y.id) > -1);
      this.getInventoryItems();
    },
    itemSubclassIds() {
      this.getInventoryItems();
    },
    itemInventoryItemId() {
      const inventoryItem = this.inventoryItems.find(x => x.id === this.itemInventoryItemId);
      if (!inventoryItem) return;
      this.itemClassId = inventoryItem.inventory_class_id;
      if (inventoryItem.inventory_sub_classes) this.itemSubclassIds = inventoryItem.inventory_sub_classes.map(x => x.id);
      else this.itemSubclassIds = [];
    }
  },
  computed: {
    isManual() {
      return !!this.bom && !!this.bom.job && this.bom.job.is_manual === 1;
    },
    jobIsActive() {
      return !!this.bom && !!this.bom.job && (this.bom.job.status_id === 1 || this.bom.job.status_id === null);
    },
    canEdit() {
      return this.isManual && this.jobIsActive;
    },
    uoms() {
      if (!this.itemInventoryItemId) return [];
      return this.inventoryItems.find(x => x.id === this.itemInventoryItemId)?.uom_items ?? [];
    },
    lengths() {
      if (!this.itemInventoryItemId) return [];
      return this.inventoryItems.find(x => x.id === this.itemInventoryItemId)?.length_items ?? [];
    },
    bomItems() {
      return this.bom?.items ?? [];
    }
  },  
  methods: {
    async open(jobId) {
      this.isInitialized = false;
      try {
        this.bom = await bomApi.openBom(jobId);
        if (this.bom === null) return this.$bus.$emit('showError', 'BOM is missing. Run Cut Sheets to generate BOM');
        this.showErrorsTabler = this.bom.import_errors.length > 0;
      } catch (e) {
        this.bom = null;
        this.$bus.$emit('showError', e.response?.data || 'Failed to open BOM');
        this.closeForm();
      } finally {
        this.isInitialized = true;
      }
    },
    closeForm() {
      this.$emit('close');
    },
    openItemForm(item = null) {
      this.itemId = item?.id ?? null;
      this.itemQuantity = item?.quantity ?? null;
      this.itemInventoryItemId = item?.inventory_item_id ?? null;
      this.itemUomId = item?.uom_id ?? null;
      this.itemLengthId = item?.length_id ?? null;
      this.itemColorId = item?.color_id ?? null;
      this.itemClassId = null;
      this.itemSubclassIds = [];
      this.showItemForm = true;
    },
    resetItemForm() {
      this.itemQuantity = null;
      this.itemInventoryItemId = null;
      this.itemColorId = null;
      this.itemUomId = null;
      this.itemLengthId = null;
      this.itemId = null;
    },
    closeItemForm() {
      this.resetItemForm();
      this.showItemForm = false;
    },
    async addItem() {
      if (!this.itemQuantity) return this.$bus.$emit('showError', 'Please enter the Quantity');
      if (!this.itemInventoryItemId) return this.$bus.$emit('showError', 'Please enter the Inventory Item');
      if (!this.itemUomId) return this.$bus.$emit('showError', 'Please enter the UOM');

      try {
        const data = {
          id: this.itemId ?? null,
          bom_id: this.bom.id,
          inventory_item_id: this.itemInventoryItemId,
          quantity: this.itemQuantity,
          uom_id: this.itemUomId,
          length_id: this.itemLengthId,
          color_id: this.itemColorId,
        };

        const res = this.itemId === null ? await bomApi.createBomItem(data) : await bomApi.updateBomItem(data);
        if (this.itemId === null) this.bom.items.push(res);
        else {
          const idx = this.bom.items.findIndex(x => x.id === this.itemId);
          if (idx > -1) {
            this.bom.items[idx].quantity = this.itemQuantity;
            this.bom.items[idx].uom_id = this.itemUomId;
            this.bom.items[idx].length_id = this.itemLengthId;
            this.bom.items[idx].color_id = this.itemColorId;
          }
        }
        this.closeItemForm();
      } catch (e) {
        console.error(e)
        console.log(e.data)
        this.$bus.$emit('showError', `Failed to ${this.itemId === null ? 'add' : 'update'} BOM item. ${e.response?.data || ''}`);
      }
    },
    async deleteItem(item) {
      try {
        const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure you want to delete this item?' });
        if (!confirm) return;
        await bomApi.deleteBomItem(item.id);
        item.is_deleted = 1;
        this.$bus.$emit('showSuccess', 'Item has been deleted');
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to delete the item');
      }
    },
    async getInventoryItems() {
      const filters = {
        inventory_class_id: this.itemClassId,
        inventory_sub_class_ids: this.itemSubclassIds,
      };
      const res = await this.$api.inventoryItem.readData({ filters });
      this.inventoryItems = res.data || [];
    },

    openItemShowImageForm(item) {
      const photoData = item.photo;
      if (!photoData) return;
      this.$refs.itemShowImageForm.openDialog(photoData.photo, photoData.mime_type, item.inventory_item.material_id);
    },
  }
};
</script>
<style scoped>

</style>
